import {
  TOOL_LABEL_OVERRIDES,
  formatToolNameAsLabel,
} from "../agentToolLabels";
import { resolveToolCategoryTitle } from "../agentToolCategoryOverrides";

export const TASK_QUEUE_PLAN_TOOL_NAME = "plan_tasks";
export const TASK_QUEUE_UPDATE_TOOL_NAME = "update_task_queue";
export const SCHEDULE_MESSAGE_TOOL_NAME = "schedule_message";
export const EDIT_SCHEDULED_MESSAGE_TOOL_NAME = "edit_scheduled_message";
export const RECURRING_SCHEDULE_TOOL_NAME = "recurring_schedule";
export const EDIT_RECURRING_SCHEDULE_TOOL_NAME = "edit_recurring_schedule";
export const CREATE_CALENDAR_EVENT_TOOL_NAME = "create_calendar_event";
export const EDIT_CALENDAR_EVENT_TOOL_NAME = "edit_calendar_event";
export const RUN_WORKFLOW_TOOL_NAME = "run_workflow";
export const READ_WORKFLOW_TOOL_NAME = "read_workflow";
export const READ_COMPONENT_TOOL_NAME = "read_component";
export const SESSION_TOOL_NAME = "get_session";
export const SEND_SLACK_COMPONENT_TOOL_NAME = "send_slack_component";
export const SEND_EMAIL_TOOL_NAME = "send_email";

export const PAIRED_TOOL_DEPENDENCIES = [
  {
    parentToolName: TASK_QUEUE_PLAN_TOOL_NAME,
    childToolName: TASK_QUEUE_UPDATE_TOOL_NAME,
  },
  {
    parentToolName: SCHEDULE_MESSAGE_TOOL_NAME,
    childToolName: EDIT_SCHEDULED_MESSAGE_TOOL_NAME,
  },
  {
    parentToolName: RECURRING_SCHEDULE_TOOL_NAME,
    childToolName: EDIT_RECURRING_SCHEDULE_TOOL_NAME,
  },
  {
    parentToolName: CREATE_CALENDAR_EVENT_TOOL_NAME,
    childToolName: EDIT_CALENDAR_EVENT_TOOL_NAME,
  },
  {
    parentToolName: RUN_WORKFLOW_TOOL_NAME,
    childToolName: READ_WORKFLOW_TOOL_NAME,
  },
  {
    parentToolName: RUN_WORKFLOW_TOOL_NAME,
    childToolName: READ_COMPONENT_TOOL_NAME,
  },
];

export const DEFAULT_ENABLED_TOOLS = [
  "read_file",
  "schedule_message",
  "edit_scheduled_message",
  "send_email",
  "web_search",
  "extract_webpage",
  "fill_form",
  "plan_tasks",
  "update_task_queue",
  "review_task",
  "get_form",
  "form_fields",
];

export const INTERNAL_ONLY_TOOL_NAMES = new Set(["get_table"]);

export const PAIRED_PARENT_TOOL_LABELS = {
  [TASK_QUEUE_PLAN_TOOL_NAME]: "Plan Tasks",
  [SCHEDULE_MESSAGE_TOOL_NAME]: "Schedule Message",
  [RECURRING_SCHEDULE_TOOL_NAME]: "Recurring Schedule",
  [CREATE_CALENDAR_EVENT_TOOL_NAME]: "Create Calendar Event",
  [RUN_WORKFLOW_TOOL_NAME]: "Run Workflow",
};

export const CATEGORY_COLORS = {
  Custom: "#795548",
  Artifacts: "#6366f1",
  Web: "#1a73e8",
  Email: "#d93025",
  Messaging: "#00897b",
  Scheduling: "#e67c11",
  Context: "#7b1fa2",
  Code: "#5c6bc0",
  General: "#4285f4",
  Agents: "#34a853",
  Workflows: "#9c27b0",
  Tables: "#f9a825",
  "Tasks & Forms": "#ea4335",
  Calendar: "#e67c11",
  Files: "#0097a7",
  Workers: "#5e35b1",
  Sessions: "#3949ab",
  Components: "#d81b60",
  "Workflow Builder": "#00838f",
};
export const CATEGORY_COLOR_FALLBACK = "#4285f4";

/** Display order for tool catalog groups (unknown categories sort after these). */
export const TOOL_CATEGORY_SORT_ORDER = [
  "Custom",
  "Artifacts",
  "Web",
  "Email",
  "Messaging",
  "Scheduling",
  "Context",
  "Code",
  "Agents",
  "Workflows",
  "Sessions",
  "Tables",
  "Tasks & Forms",
  "Files",
  "Calendar",
  "General",
];

function compareToolCategoryTitles(a, b) {
  const ai = TOOL_CATEGORY_SORT_ORDER.indexOf(a);
  const bi = TOOL_CATEGORY_SORT_ORDER.indexOf(b);
  const aRank = ai === -1 ? TOOL_CATEGORY_SORT_ORDER.length : ai;
  const bRank = bi === -1 ? TOOL_CATEGORY_SORT_ORDER.length : bi;
  if (aRank !== bRank) return aRank - bRank;
  return a.localeCompare(b);
}

export const TABLE_SCOPE_TOOL_NAMES = new Set([
  "add_column",
  "rename_column",
  "delete_column",
  "create_row",
  "update_row",
  "query_table",
]);

const HIDDEN_TOOL_CATEGORIES = new Set([
  "Workflow Builder",
  "Components",
]);

const normalizeToolList = (enabledTools) =>
  Array.isArray(enabledTools)
    ? [
        ...new Set(
          enabledTools.filter(
            (toolName) => typeof toolName === "string" && toolName.trim()
          )
        ),
      ]
    : [];

export function ensureTaskQueuePlannerToolsPaired(enabledTools) {
  const dedupedTools = normalizeToolList(enabledTools);
  return PAIRED_TOOL_DEPENDENCIES.reduce((tools, dependency) => {
    const withoutStandaloneChild = tools.filter(
      (toolName) => toolName !== dependency.childToolName
    );
    if (withoutStandaloneChild.includes(dependency.parentToolName)) {
      return [...withoutStandaloneChild, dependency.childToolName];
    }
    return withoutStandaloneChild;
  }, dedupedTools);
}

function applyPairedToolHierarchy(tools) {
  if (!Array.isArray(tools) || tools.length === 0) {
    return [];
  }
  let normalizedTools = tools.map((tool) => ({ ...tool }));

  PAIRED_TOOL_DEPENDENCIES.forEach((dependency) => {
    const parentIndex = normalizedTools.findIndex(
      (tool) => tool.name === dependency.parentToolName
    );
    const childIndex = normalizedTools.findIndex(
      (tool) => tool.name === dependency.childToolName
    );
    if (childIndex < 0) {
      return;
    }

    const childTool = {
      ...normalizedTools[childIndex],
      parentToolName: dependency.parentToolName,
      disableStandaloneToggle: true,
    };
    normalizedTools.splice(childIndex, 1);

    if (parentIndex < 0) {
      normalizedTools.push(childTool);
      return;
    }

    const nextParentIndex = normalizedTools.findIndex(
      (tool) => tool.name === dependency.parentToolName
    );
    normalizedTools.splice(nextParentIndex + 1, 0, childTool);
  });

  return normalizedTools;
}

export const mapToolCatalogToCategories = (catalog) => {
  if (!Array.isArray(catalog) || catalog.length === 0) return [];

  const grouped = new Map();
  catalog.forEach((entry) => {
    const toolName = entry?.toolName || entry?.function?.name || entry?.name;
    if (!toolName) return;
    if (INTERNAL_ONLY_TOOL_NAMES.has(toolName)) return;
    const fallbackLabel =
      TOOL_LABEL_OVERRIDES[toolName] || formatToolNameAsLabel(toolName);
    const normalizedCategory =
      toolName === SESSION_TOOL_NAME
        ? "Workflows"
        : entry?.category || (entry?.toolName ? "General" : "Available Tools");
    const normalizedLabel =
      toolName === SESSION_TOOL_NAME
        ? "Get Workflow Sessions"
        : TOOL_LABEL_OVERRIDES[toolName] ||
          entry?.displayName ||
          entry?.label ||
          fallbackLabel;
    const rawCategory =
      normalizedCategory || (entry?.toolName ? "General" : "Available Tools");
    const category = resolveToolCategoryTitle(toolName, rawCategory);
    if (HIDDEN_TOOL_CATEGORIES.has(category)) return;
    if (!grouped.has(category)) grouped.set(category, []);
    grouped.get(category).push({
      name: toolName,
      label: normalizedLabel,
      desc:
        entry?.description || entry?.desc || entry?.function?.description || "",
      iconName: entry?.iconName || null,
      parentToolName: entry?.parentToolName || null,
      requiresTableScope: Boolean(entry?.requiresTableScope),
      disableStandaloneToggle: Boolean(entry?.disableStandaloneToggle),
    });
  });

  return Array.from(grouped.entries())
    .map(([title, tools]) => ({
      title,
      tools: applyPairedToolHierarchy(
        tools.sort((a, b) => a.label.localeCompare(b.label))
      ),
    }))
    .filter(
      (category) => Array.isArray(category.tools) && category.tools.length
    )
    .sort((a, b) => compareToolCategoryTitles(a.title, b.title));
};

export const filterToolCategories = (categories, rawQuery) => {
  if (!Array.isArray(categories) || categories.length === 0) return [];
  const query = String(rawQuery || "")
    .trim()
    .toLowerCase();
  if (!query) return categories;

  return categories
    .map((category) => {
      const tools = Array.isArray(category?.tools) ? category.tools : [];
      const filteredTools = tools.filter((tool) =>
        [tool?.label, tool?.name, tool?.desc]
          .map((value) => String(value || "").toLowerCase())
          .join(" ")
          .includes(query)
      );
      return {
        ...category,
        tools: filteredTools,
      };
    })
    .filter((category) => category.tools.length > 0);
};

export { formatToolNameAsLabel };
